import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { orgUserProfilePhoto } from '../../lib/profileAvatar.js';
import {
  CLIENT_ROLES,
  ROLE_AREA_LABELS,
  ROLE_RESTRICTIONS,
  SEAT_ADDON_PRICING,
  createOrgInvite,
  displayPersonName,
  formatSeatAddonPrice,
  seatInviteConfirm,
  seatRemoveConfirm,
} from '../../lib/settingsStore.js';

function personInitial(user) {
  const source = displayPersonName(user) || '?';
  return (source[0] || '?').toUpperCase();
}

function roleLabel(roleId) {
  return CLIENT_ROLES.find((item) => item.id === roleId)?.label || roleId;
}

function PersonAvatar({ user, accountEmail, accountAvatar, isOwner }) {
  const photo = orgUserProfilePhoto(user, { email: accountEmail, avatarDataUrl: accountAvatar });
  const className = [
    'users-person-avatar',
    isOwner ? 'is-owner' : '',
    photo ? 'has-photo' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={className}>
      {photo ? <img src={photo} alt="" /> : personInitial(user)}
    </span>
  );
}

export default function UsersRolesPanel({
  orgUsers,
  accountEmail = '',
  accountAvatar = '',
  onInvite,
  onResend,
  onUpdateRole,
  onRemove,
  canManage = true,
  shareMessages = false,
  canManageSharing = false,
  onShareMessages,
  pending,
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('MEMBER');
  const [showInvite, setShowInvite] = useState(false);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const [sending, setSending] = useState(false);
  const [resendingId, setResendingId] = useState('');
  const [pendingInvite, setPendingInvite] = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null);
  const confirmButtonRef = useRef(null);
  const unitPrice = formatSeatAddonPrice(SEAT_ADDON_PRICING.extraSeatMonthly);
  const inviteRole = CLIENT_ROLES.find((item) => item.id === role) || CLIENT_ROLES[2];
  const inviteAreas = ROLE_RESTRICTIONS[inviteRole.id] || [];
  const inviteConfirm = pendingInvite ? seatInviteConfirm(orgUsers, pendingInvite) : null;
  const removeConfirm = pendingRemove ? seatRemoveConfirm(orgUsers, pendingRemove) : null;
  const seatConfirm = inviteConfirm || removeConfirm;

  function closeSeatConfirm() {
    if (sending) return;
    setPendingInvite(null);
    setPendingRemove(null);
  }

  useEffect(() => {
    if (!pendingInvite && !pendingRemove) return undefined;
    confirmButtonRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape' && !sending) {
        setPendingInvite(null);
        setPendingRemove(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingInvite, pendingRemove, sending]);

  function submitInvite(e) {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    if (orgUsers.some((u) => u.email === trimmed)) {
      setError('That user is already on this account.');
      return;
    }
    setPendingInvite(createOrgInvite({ email: trimmed, name, role }));
  }

  async function confirmInvite() {
    if (!pendingInvite || sending) return;
    setSending(true);
    setError('');
    try {
      await onInvite(pendingInvite);
      setEmail('');
      setName('');
      setRole('MEMBER');
      setShowInvite(false);
      setPendingInvite(null);
    } catch (err) {
      setPendingInvite(null);
      setError(err.message || 'Could not send the invite email.');
    } finally {
      setSending(false);
    }
  }

  function confirmRemove() {
    if (!pendingRemove || sending) return;
    onRemove(pendingRemove.id);
    setPendingRemove(null);
  }

  async function resendInvite(user) {
    setListError('');
    setResendingId(user.id);
    try {
      await onResend(user);
    } catch (err) {
      setListError(err.message || 'Could not resend the invite email.');
    } finally {
      setResendingId('');
    }
  }

  return (
    <div className="settings-form">
      <div className="users-roles-head">
        <div>
          <h2>Users &amp; Roles</h2>
          <p className="settings-lead">
            {canManage
              ? 'Invite teammates and choose what each person can open. The owner seat is included.'
              : 'People in this organization. The owner manages invites and roles.'}
          </p>
        </div>
        {canManage ? (
          <button
            type="button"
            className="btn-primary users-invite-btn"
            onClick={() => setShowInvite((open) => !open)}
          >
            Invite user
          </button>
        ) : null}
      </div>

      <div className="settings-subsection">
        <h3>Messages</h3>
        <p className="settings-sublead">
          Choose whether messages on a demand are visible across the organization.
        </p>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(shareMessages)}
            disabled={(!canManage && !canManageSharing) || pending}
            onChange={(e) => onShareMessages?.(e.target.checked)}
          />
          <span>
            <strong>Share organization messages</strong>
            <small>Everyone in the organization can read messages on a demand.</small>
          </span>
        </label>
      </div>

      <h3 className="settings-inline-title">People</h3>

      {showInvite ? (
        <form className="invite-box users-invite-box" onSubmit={submitInvite}>
          <h3>Invite user</h3>
          <p className="settings-sublead">
            This invite adds an extra-user add-on at <strong>{unitPrice}/mo</strong>. Manage charges
            anytime in{' '}
            <Link className="text-link strong" to="?tab=billing">
              Billing
            </Link>
            .
          </p>
          {error ? <p className="form-error">{error}</p> : null}
          <div className="settings-grid">
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@company.com"
                required
              />
            </label>
            <label className="field">
              <span>Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jordan Lee"
              />
            </label>
            <div className="field field-span-2">
              <label className="users-invite-role-label" htmlFor="invite-role">Role</label>
              <select
                id="invite-role"
                value={role}
                aria-describedby="invite-role-access"
                onChange={(e) => setRole(e.target.value)}
              >
                {CLIENT_ROLES.filter((item) => item.id !== 'OWNER').map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
              <div id="invite-role-access" className="users-role-detail users-invite-role">
                <h4>{inviteRole.label}</h4>
                <p>{inviteRole.description}</p>
                <div className="users-role-chips">
                  {inviteAreas.map((area) => (
                    <span key={area} className="users-role-chip">
                      {ROLE_AREA_LABELS[area] || area}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="drawer-actions">
            <button type="button" className="btn-ghost" onClick={() => setShowInvite(false)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={pending || sending}>
              {sending ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
        </form>
      ) : null}

      {listError ? <p className="form-error">{listError}</p> : null}

      {seatConfirm ? (
        <div
          className="social-modal-backdrop"
          role="presentation"
          onClick={closeSeatConfirm}
        >
          <div
            className="social-modal billing-plan-confirm users-invite-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="seat-confirm-title"
            aria-describedby="seat-confirm-note"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="seat-confirm-title">{seatConfirm.title}</h2>
            <p className="users-invite-confirm-who">
              <strong>{seatConfirm.person}</strong>
              <span>{seatConfirm.roleLabel}</span>
            </p>
            <div className="billing-plan-confirm-swap">
              <div className="billing-plan-confirm-plan">
                <span>Now</span>
                <strong>{seatConfirm.nowLabel}</strong>
                <em>{seatConfirm.nowPriceLabel}</em>
              </div>
              <div className="billing-plan-confirm-arrow" aria-hidden="true">
                →
              </div>
              <div className="billing-plan-confirm-plan is-next">
                <span>New</span>
                <strong>{seatConfirm.nextLabel}</strong>
                <em>{seatConfirm.deltaLabel}</em>
                <em>{seatConfirm.nextPriceLabel} total</em>
              </div>
            </div>
            <p id="seat-confirm-note">{seatConfirm.note}</p>
            <div className="billing-plan-confirm-actions">
              <button
                type="button"
                className="btn-ghost"
                onClick={closeSeatConfirm}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                ref={confirmButtonRef}
                disabled={pending || sending}
                onClick={inviteConfirm ? confirmInvite : confirmRemove}
              >
                {sending ? 'Sending…' : seatConfirm.action}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ul className="users-people">
        {orgUsers.map((user) => {
          const isOwner = user.role === 'OWNER';
          return (
            <li key={user.id} className="users-person">
              <PersonAvatar
                user={user}
                accountEmail={accountEmail}
                accountAvatar={accountAvatar}
                isOwner={isOwner}
              />
              <div className="users-person-body">
                <strong>{displayPersonName(user)}</strong>
                {displayPersonName(user).toLowerCase() === String(user.email || '').trim().toLowerCase() ? null : (
                  <span className="muted-line">{user.email}</span>
                )}
                <small className="field-hint">
                  {user.status === 'INVITED' ? 'Invite pending' : 'Active'}
                  {isOwner ? ' · included seat' : ` · ${unitPrice}/mo add-on`}
                  {user.invitedAt
                    ? ` · since ${new Date(user.invitedAt).toLocaleDateString()}`
                    : ''}
                </small>
              </div>
              {isOwner ? (
                <span className="users-owner-pill">Owner</span>
              ) : !canManage ? (
                <span className="users-role-pill">{roleLabel(user.role)}</span>
              ) : (
                <div className="users-person-actions">
                  <select
                    value={user.role}
                    onChange={(e) => onUpdateRole(user.id, e.target.value)}
                    aria-label={`Role for ${user.email}`}
                  >
                    {CLIENT_ROLES.filter((item) => item.id !== 'OWNER').map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  {user.status === 'INVITED' ? (
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => resendInvite(user)}
                      disabled={pending || sending || resendingId === user.id}
                    >
                      {resendingId === user.id ? 'Sending…' : 'Resend'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setPendingRemove(user)}
                    disabled={pending || sending || Boolean(resendingId)}
                  >
                    Remove
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
